import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Container, Sprite, Text } from "@pixi/react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setIntroTransitionActive,
  setUserInterfaceDisabled,
  toggleFullscreen,
} from "../../../../../../State/actions";
import {
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectIsHoldAndWinMidFeatureReload,
} from "../../../../../../State/selectors";
import { SOUND_NAME } from "../../../../../../sounds";
import { useDimensions } from "../../../../../../Contexts/DimensionsContext";
import { useInitialIdleAnimations, useHoldAndWinIdleAnimations } from "../../../../../../Hooks/useLandingAnimations";
import * as PIXI from "pixi.js";
import { soundManager } from "../../../../../../SoundManager";
import { isIOS } from "../../../../../../utilities";
import TextButton from "../../../../primary/Buttons/TextButton";
import IntroPopupController, { getStaticConfig } from "./IntroPopupController";
import { safeInit } from "../../../../../../utilities";
import ButtonSound from "../../../../secondary/Buttons/ButtonSound";
import { usePopiToolbarDimensions } from "../../../../../../Hooks/usePopiToolbarDimensions";
import { PixiSettings } from "../../../../../../pixiSettings";
import { ASSET_NAME } from "../../../../../../assets";
import { FONT_CONFIG } from "../../../../../../textureConfigs";

/**
 * IntroPopup component - Displays intro screen with spine animations or static textures
 * @param {string} renderMode - "spine" (default) for spine animations, "static" for static textures
 *
 * Static mode renders feature_01.png and feature_02.png:
 * - Desktop/Landscape: Side by side horizontally
 * - Portrait: Stacked vertically
 *
 * Fine-tune sizing via config objects in IntroPopupController.js:
 * - STATIC_CONFIG_DESKTOP
 * - STATIC_CONFIG_TABLET_LANDSCAPE
 * - STATIC_CONFIG_TABLET_PORTRAIT
 * - STATIC_CONFIG_MOBILE_LANDSCAPE
 * - STATIC_CONFIG_MOBILE_PORTRAIT
 */
const PixiIntroPopup = ({ renderMode = "spine" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const introPopupActive = useSelector(selectIntroPopupActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);

  const { dimensions, uiSymbolSize, device, orientation } = useDimensions();
  const toolbar = usePopiToolbarDimensions();
  const { fullscreenSupported } = useSelector((state) => state.settings);
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { triggerHoldAndWinIdleAnimations } = useHoldAndWinIdleAnimations();
  const isHoldAndWinMidFeatureReload = useSelector(selectIsHoldAndWinMidFeatureReload);

  // Force re-render when fonts load to apply correct font to text
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Container ref for spine controller
  const containerRef = useRef(null);
  // Spine controller
  const introControllerRef = useRef(null);

  // Force re-render once fonts are loaded to apply correct font
  useEffect(() => {
    if (!introPopupActive) return;

    const checkFonts = async () => {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
        // Wait one frame to ensure fonts are fully processed
        await new Promise((resolve) => requestAnimationFrame(resolve));
        setFontsLoaded(true);
      } else {
        // Font Loading API not available - assume fonts are loaded
        await new Promise((resolve) => requestAnimationFrame(resolve));
        setFontsLoaded(true);
      }
    };

    checkFonts();
  }, [introPopupActive]);

  // Handle popup cleanup when closed
  useEffect(() => {
    // Cleanup spine when popup closes
    if (!introPopupActive && introControllerRef.current) {
      introControllerRef.current.cleanup();
      introControllerRef.current = null;
    }
  }, [introPopupActive]);

  // Initialize spine controller when popup becomes active (only in spine mode)
  useEffect(() => {
    if (
      renderMode === "spine" &&
      introPopupActive &&
      containerRef.current &&
      !introControllerRef.current
    ) {
      safeInit(() => {
        introControllerRef.current = new IntroPopupController(
          containerRef.current,
          dimensions,
          uiSymbolSize,
          { device, orientation },
          t
        );
      });
    }
    dispatch(setUserInterfaceDisabled(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPopupActive, renderMode]);

  // Handle dimension changes and device/orientation changes (only when popup is active and in spine mode)
  useEffect(() => {
    if (
      renderMode === "spine" &&
      introPopupActive &&
      introControllerRef.current
    ) {
      introControllerRef.current.updateDimensions(dimensions, uiSymbolSize, {
        device,
        orientation,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPopupActive, uiSymbolSize, device, orientation, renderMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (introControllerRef.current) {
        introControllerRef.current.cleanup();
        introControllerRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(
    (event) => {
      event.stopPropagation();
      soundManager.play(SOUND_NAME.button_click);

      // Enable fullscreen on non-desktop devices (if supported and not iOS)
      const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
      if (!isDesktop && !isIOS() && fullscreenSupported) {
        dispatch(toggleFullscreen());
      }

      // Trigger initial idle animations immediately when user clicks
      // Game is already rendered underneath at alpha 0.001
      // For mid-feature reload ONLY, trigger both base game and Hold and Win idle animations
      if (isHoldAndWinMidFeatureReload) {
        triggerInitialIdleAnimations();
        // Wait 2 frames for Hold and Win components to be fully ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            triggerHoldAndWinIdleAnimations();
          });
        });
      } else {
        // For intro reload and normal base game, trigger initial idle animations
        // (intro reload will show HoldAndWinIntroDialog which triggers landing animations)
        triggerInitialIdleAnimations();
      }

      dispatch(setIntroTransitionActive(true));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, device, fullscreenSupported, triggerInitialIdleAnimations, triggerHoldAndWinIdleAnimations]
  );

  // Keyboard event listener for space bar
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when intro popup is active and UI is not disabled
      if (!introPopupActive || userInterfaceDisabled) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Execute the click handler
      handleClick(event);
    };

    // Use capture phase to ensure we get the event first, only when popup is active
    if (introPopupActive && !userInterfaceDisabled) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [introPopupActive, userInterfaceDisabled, handleClick]);

  // Calculate static sprite positions and dimensions
  const getStaticSpriteConfig = () => {
    if (renderMode !== "static") return null;

    const config = getStaticConfig(device, orientation);
    const texture1 = PIXI.Assets.get(ASSET_NAME.feature_01);
    const texture2 = PIXI.Assets.get(ASSET_NAME.feature_02);

    if (!texture1 || !texture2) return null;

    // Calculate dimensions - width controls size, height scales proportionally
    const baseWidth = texture1.width * config.widthMultiplier;
    const aspectRatio = texture1.height / texture1.width;
    const baseHeight = baseWidth * aspectRatio; // Height scales with width to maintain aspect ratio

    // Calculate dynamic spacing and offsets based on uiSymbolSize
    const spacingInPixels = config.spacing * uiSymbolSize;
    const horizontalOffsetInPixels = config.horizontalOffset * uiSymbolSize;
    const verticalOffsetInPixels = config.verticalOffset * uiSymbolSize;

    let sprite1X, sprite1Y, sprite2X, sprite2Y;

    if (config.layout === "horizontal") {
      // Side by side - centered with spacing between them
      const centerX = dimensions.width / 2 + horizontalOffsetInPixels;
      const centerY = dimensions.height / 2 + verticalOffsetInPixels;
      const halfSpacing = spacingInPixels / 2;

      // Position sprites on either side of center with spacing
      sprite1X = centerX - halfSpacing - baseWidth / 2;
      sprite1Y = centerY;
      sprite2X = centerX + halfSpacing + baseWidth / 2;
      sprite2Y = centerY;
    } else {
      // Vertical - centered with spacing between them
      const centerX = dimensions.width / 2 + horizontalOffsetInPixels;
      const centerY = dimensions.height / 2 + verticalOffsetInPixels;
      const halfSpacing = spacingInPixels / 2;

      // Position sprites above and below center with spacing
      sprite1X = centerX;
      sprite1Y = centerY - halfSpacing - baseHeight / 2;
      sprite2X = centerX;
      sprite2Y = centerY + halfSpacing + baseHeight / 2;
    }

    return {
      texture1,
      texture2,
      width: baseWidth,
      height: baseHeight,
      sprite1X,
      sprite1Y,
      sprite2X,
      sprite2Y,
    };
  };

  const staticConfig = renderMode === "static" ? getStaticSpriteConfig() : null;

  // Calculate text font size (20% of sprite height for testing)
  const featureTitleFontSize = useMemo(() => {
    if (!staticConfig?.height) return 20;
    const baseFontSize = staticConfig.height * 0.0325; // 20% of sprite height
    return Math.max(12, Math.floor(baseFontSize)); // Minimum 12px
  }, [staticConfig]);

  // Create text style for feature titles (yellow with drop shadow)
  const featureTitleTextStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.TERTIARY,
        fontSize: featureTitleFontSize,
        fontWeight: "bold",
        fill: 0xffe462, // Yellow color
        dropShadow: true,
        dropShadowColor: 0x4a0900,
        dropShadowDistance: 3,
        dropShadowBlur: 4,
        dropShadowAlpha: 1,
        dropShadowAngle: Math.PI / 2,
        align: "center",
        wordWrap: true,
        wordWrapWidth: staticConfig ? staticConfig.width * 0.4 : 300,
        breakWords: false,
      }),
    [featureTitleFontSize, staticConfig]
  );

  // Don't render anything if popup is not active
  if (!introPopupActive) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      name="pixi_intro_popup"
      x={0}
      y={0}
      zIndex={100}
    >
      {/* Full-screen invisible background for click handling */}
      <Sprite
        texture={PIXI.Texture.EMPTY}
        x={0}
        y={0}
        width={dimensions.width}
        height={dimensions.height}
        zIndex={1}
        eventMode="static"
        cursor="pointer"
        pointertap={handleClick}
      />

      {/* Static sprites - rendered when renderMode is "static" */}
      {renderMode === "static" && staticConfig && (
        <>
          {/* Feature 1 Container */}
          <Container
            x={staticConfig.sprite1X}
            y={staticConfig.sprite1Y}
            name="feature_1_container"
          >
            {/* Sprite at top */}
            <Sprite
              texture={staticConfig.texture1}
              width={staticConfig.width}
              height={staticConfig.height}
              anchor={{ x: 0.5, y: 0 }}
              x={0}
              y={-staticConfig.height / 2}
              zIndex={2}
            />
            {/* Text below sprite */}
            <Text
              text={t("start_page_1_title")}
              style={featureTitleTextStyle}
              anchor={{ x: 0.5, y: 0 }}
              x={0}
              y={staticConfig.height * 0.275}
              resolution={2}
              zIndex={3}
            />
          </Container>

          {/* Feature 2 Container */}
          <Container
            x={staticConfig.sprite2X}
            y={staticConfig.sprite2Y}
            name="feature_2_container"
          >
            {/* Sprite at top */}
            <Sprite
              texture={staticConfig.texture2}
              width={staticConfig.width}
              height={staticConfig.height}
              anchor={{ x: 0.5, y: 0 }}
              x={0}
              y={-staticConfig.height / 2}
              zIndex={2}
            />
            {/* Text below sprite */}
            <Text
              text={t("start_page_2_title")}
              style={featureTitleTextStyle}
              anchor={{ x: 0.5, y: 0 }}
              x={0}
              y={staticConfig.height * 0.275}
              resolution={2}
              zIndex={3}
            />
          </Container>
        </>
      )}

      {/* Sound button - must be after backdrop to have higher zIndex */}
      <ButtonSound toolbar={toolbar} zIndex={10} />

      {/* TextButton only shown in static mode - spine mode handles text via bones */}
      {renderMode === "static" && (
        <TextButton
          key={fontsLoaded ? "fonts-loaded" : "fonts-loading"}
          x={dimensions.width / 2}
          y={dimensions.height}
          translationKey="tap_to_start"
          uppercase={true}
        />
      )}
    </Container>
  );
};

export default PixiIntroPopup;
